import {
	type CSSProperties,
	type PointerEvent as ReactPointerEvent,
	useRef,
	useState,
} from "react";
import { IDENTITY_TRANSFORM, MIN_SCALE } from "web_myplann/Chat/constants";
import { clampOffset } from "web_myplann/Chat/Helpers/PinchZoom/clampOffset";
import { distanceBetween } from "web_myplann/Chat/Helpers/PinchZoom/distanceBetween";
import { getContainedSize } from "web_myplann/Chat/Helpers/PinchZoom/getContainedSize";
import { getNaturalSize } from "web_myplann/Chat/Helpers/PinchZoom/getNaturalSize";
import { getPanBounds } from "web_myplann/Chat/Helpers/PinchZoom/getPanBounds";
import { getPanTransform } from "web_myplann/Chat/Helpers/PinchZoom/getPanTransform";
import { getPinchTransform } from "web_myplann/Chat/Helpers/PinchZoom/getPinchTransform";
import { toTransformCss } from "web_myplann/Chat/Helpers/PinchZoom/toTransformCss";
import { zoomAroundPoint } from "web_myplann/Chat/Helpers/PinchZoom/zoomAroundPoint";
import type {
	PanStart,
	PinchStart,
	Point,
	Transform,
} from "web_myplann/Chat/Types/pinchZoom";

const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MAX_DELAY_MS = 300;
const DOUBLE_TAP_MAX_DISTANCE_PX = 40;
const TAP_MAX_MOVEMENT_PX = 12;
const SNAP_BACK_BELOW_SCALE = 1.05;
const SETTLE_TRANSITION = "transform 200ms ease-out";

type Tap = { time: number; point: Point };

export const usePinchZoom = () => {
	const containerRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);
	const pointers = useRef(new Map<number, Point>());
	const pinchStart = useRef<PinchStart | null>(null);
	const panStart = useRef<PanStart | null>(null);
	const pressStart = useRef<Point | null>(null);
	const lastTap = useRef<Tap | null>(null);
	const transformRef = useRef<Transform>(IDENTITY_TRANSFORM);
	const [transform, setTransform] = useState<Transform>(IDENTITY_TRANSFORM);
	const [isGesturing, setIsGesturing] = useState(false);

	const constrain = (next: Transform): Transform => {
		const container = containerRef.current?.getBoundingClientRect();
		if (!container) return next;
		const content = getContainedSize(
			container,
			getNaturalSize(imageRef.current),
		);
		return clampOffset(next, getPanBounds(container, content, next.scale));
	};

	const previewTransform = (next: Transform) => {
		transformRef.current = constrain(next);
		if (imageRef.current) {
			imageRef.current.style.transform = toTransformCss(transformRef.current);
		}
	};

	const commitTransform = (next: Transform) => {
		transformRef.current = constrain(next);
		setTransform(transformRef.current);
	};

	const toLocalPoint = (event: ReactPointerEvent<HTMLDivElement>): Point => {
		const rect = containerRef.current?.getBoundingClientRect();
		if (!rect) return { x: event.clientX, y: event.clientY };
		return {
			x: event.clientX - (rect.left + rect.width / 2),
			y: event.clientY - (rect.top + rect.height / 2),
		};
	};

	const activePoints = () => [...pointers.current.values()];

	const finishGesture = () => {
		pinchStart.current = null;
		panStart.current = null;
		pressStart.current = null;
		setIsGesturing(false);
		commitTransform(
			transformRef.current.scale < SNAP_BACK_BELOW_SCALE
				? IDENTITY_TRANSFORM
				: transformRef.current,
		);
	};

	const handleTap = (point: Point) => {
		const now = Date.now();
		const previous = lastTap.current;
		const isDoubleTap =
			previous !== null &&
			now - previous.time <= DOUBLE_TAP_MAX_DELAY_MS &&
			distanceBetween(previous.point, point) <= DOUBLE_TAP_MAX_DISTANCE_PX;

		if (!isDoubleTap) {
			lastTap.current = { time: now, point };
			return;
		}

		lastTap.current = null;
		commitTransform(
			transformRef.current.scale > MIN_SCALE
				? IDENTITY_TRANSFORM
				: zoomAroundPoint(IDENTITY_TRANSFORM, point, DOUBLE_TAP_SCALE),
		);
	};

	const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		event.currentTarget.setPointerCapture(event.pointerId);
		const point = toLocalPoint(event);
		pointers.current.set(event.pointerId, point);
		const points = activePoints();

		if (points.length === 2) {
			pinchStart.current = {
				distance: distanceBetween(points[0], points[1]),
				transform: transformRef.current,
			};
			panStart.current = null;
			pressStart.current = null;
		} else if (points.length === 1) {
			panStart.current = { point, transform: transformRef.current };
			pressStart.current = point;
		}
		setIsGesturing(true);
	};

	const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (!pointers.current.has(event.pointerId)) return;
		const point = toLocalPoint(event);
		pointers.current.set(event.pointerId, point);
		const points = activePoints();

		if (points.length >= 2 && pinchStart.current) {
			previewTransform(
				getPinchTransform(pinchStart.current, points[0], points[1]),
			);
			return;
		}

		const pan = panStart.current;
		if (points.length === 1 && pan && pan.transform.scale > MIN_SCALE) {
			previewTransform(getPanTransform(pan, point));
		}
	};

	const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (!pointers.current.has(event.pointerId)) return;
		const point = toLocalPoint(event);
		pointers.current.delete(event.pointerId);
		const remaining = activePoints();

		if (remaining.length === 1) {
			pinchStart.current = null;
			panStart.current = {
				point: remaining[0],
				transform: transformRef.current,
			};
			pressStart.current = null;
			return;
		}
		if (remaining.length > 0) return;

		const isTap =
			pressStart.current !== null &&
			distanceBetween(pressStart.current, point) <= TAP_MAX_MOVEMENT_PX;
		finishGesture();
		if (isTap) handleTap(point);
	};

	const onPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
		pointers.current.delete(event.pointerId);
		if (pointers.current.size === 0) finishGesture();
	};

	const style: CSSProperties = {
		transform: toTransformCss(transform),
		transition: isGesturing ? "none" : SETTLE_TRANSITION,
	};

	return {
		containerRef,
		imageRef,
		handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
		style,
	};
};
